import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField } from "@/components/FormField";
import { api, formatApiError } from "@/lib/api";
import { ui } from "@/lib/ui";

const blank = { name: "", email: "", password: "", role: "employee" };

export const AddEmployeeDialog = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      toast.success("Staff account created", { description: `${form.email} can now sign in.` });
      setForm(blank);
      setOpen(false);
      onAdded(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={`${ui.btnPrimary} h-10`} data-testid="add-employee-open-modal-button">
          <UserPlus className="w-4 h-4 mr-2" />Add staff
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-[#070B16] border glow-cyan text-slate-100 sm:max-w-md" data-testid="add-employee-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest">Add staff</DialogTitle>
          <DialogDescription className="text-slate-400">They sign in at Staff access with this email and password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormField id="emp-name" label="Name">
            <Input id="emp-name" required value={form.name} onChange={set("name")} className={ui.input} data-testid="add-employee-name-input" />
          </FormField>
          <FormField id="emp-email" label="Email">
            <Input id="emp-email" type="email" required value={form.email} onChange={set("email")} className={ui.input} data-testid="add-employee-email-input" />
          </FormField>
          <FormField id="emp-password" label="Password (min 6)">
            <Input id="emp-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className={ui.input} data-testid="add-employee-password-input" />
          </FormField>
          <FormField id="emp-role" label="Role">
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger id="emp-role" className={ui.input} data-testid="add-employee-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className={ui.menu}>
                <SelectItem value="employee" data-testid="add-employee-role-employee">Employee (dispatcher / driver)</SelectItem>
                <SelectItem value="admin" data-testid="add-employee-role-admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <Button type="submit" disabled={busy} className={`${ui.btnPrimary} w-full h-11`} data-testid="add-employee-submit-button">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create account
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
